import AxiosModules from 'axios';
import Qs from 'qs';
import { ElMessage, ElMessageBox } from 'element-plus';
import { isElectron } from '@/utils/electron';
import { resolveOsHeader } from '@/utils/embedEntry';
import { startWorkBuddySessionRecovery } from '@/utils/workBuddySessionRecovery';
import { isAuthTransitioning } from '@/utils/authTransition';
import { refreshAccessTokenExclusively } from '@/utils/brmTokenRefreshLock';
import { trackGlobalAlert, ALERT_CHANNEL, ALERT_SCENE } from '@/utils/globalAlertTrack';
import { isMac, isWindows } from '@/utils/device';

import store from '@/store';
import router from '@/router';
import { handleResponseTrack } from '@/jmHttp/index';
import { LOCAL_STORAGE_KEY, DEFAULT_REALM, LOGIN_INVALID_CODES } from '@/constants';
import { storage, getStorageAuthInfo } from '@/utils/storageOperateApi';
import { isAccessTokenExpired, isOverseasEnv } from '@/utils/api';
import { decryptRequest } from '@/utils/decryptRequest';
import { isBuild } from './host';
import { getCookie } from '@/utils/cookie';
import i18n from '@/plugins/i18n';
import slsLogger, { generateGUID } from '@/utils/slsLogger';
import { getOverseaHeader } from '@/utils/oversea';

// 此接口使用进门的token，生成外部服务的JWT
const AUTH = '/comein/usercenter/json/auth/accessExternalService';

/** 接口版本号 **/
export const PORTVERSION = '1.2.6';
/** 加密的盐 */
let requestSalt = isBuild ? '686b7fa5e94da8312fb2b3c7e11c8e73' : '71a47b8048104442bb5e69069fd75a5f';
if (isElectron) {
  requestSalt = isBuild ? 'd024485429d943febe86fc44f5dd87a1' : 'faf747425c17458db603e119c7429b17';
}
export const REQUESTSALT = requestSalt;

const axios = AxiosModules.create();

axios.defaults.timeout = 120000;
axios.defaults.retry = 0; // 2021-04-22 暂时关闭超时重试机制、创建接口或者短信接口会触发多次
axios.defaults.retryDelay = 1000;
axios.defaults.retryCount = 0;

let isLoginOut = false;
/** ERR_NETWORK 全局只弹一次，恢复连通后清除（含拦截器 reject 的 response） */
let networkErrorToastShown = false;
/**
 * 可由 WorkBuddy 静默恢复接管的登录失效码。
 * 不含 401：该码只提示无权限、不退出登录，本就不需要重建登录态。
 */
const SESSION_RECOVERABLE_CODES = new Set(['201', '20101', '20102', '403']);

/**
 * 本次错误处理的弹框上下文（触发场景与请求配置）。
 * 由各触发点在同一条同步调用链上写入、alertFunc 立即消费，不存在并发覆盖。
 */
let alertContext = {};

/**
 * 取出并清空弹框上下文，避免上一次未弹框的上下文残留到下一次上报。
 * @returns {{ scene?: string, config?: object }} 本次上下文，未设置时为空对象
 */
function consumeAlertContext() {
  const context = alertContext;
  alertContext = {};
  return context;
}

const alertFunc = function (
  code,
  message,
  bolRedirect = true,
  confirmText = i18n.global.t('common.button.confirm'),
  nodeRefresh = true,
  callback
) {
  const context = consumeAlertContext();
  if (isLoginOut || ([201, 403].includes(code) && store.state.isLogin)) return;
  // 本机正在重新登录换发鉴权：本地 token 处于空窗期，此刻的失效码是假象，弹框会把用户又赶回登录页一次
  if (LOGIN_INVALID_CODES.has(String(code)) && isAuthTransitioning()) return;
  // WorkBuddy 会话能凭 wb.login 静默重建登录态，不必弹框把用户赶回登录页
  if (SESSION_RECOVERABLE_CODES.has(String(code)) && startWorkBuddySessionRecovery()) return;
  isLoginOut = true;
  // 这个框会把用户赶回登录页，上报触发链路与场景，便于线上还原「为什么又让我重新登录」
  trackGlobalAlert({ channel: ALERT_CHANNEL.BRM, code, message, ...context });
  ElMessageBox.alert(message, i18n.global.t('common.tip.tips'), {
    customClass: 'w480 message-box-normal',
    confirmButtonText: instance?.app?.config?.globalProperties?.$bridge?.reLoginInfo?.confirmText || confirmText,
    callback: async () => {
      isLoginOut = false;
      if (callback) return callback();
      if (nodeRefresh) return true;
      await store.dispatch('logoutAction');

      refreshSubscribers.length = 0;

      // 合作机构存在自定义跳转事件则执行
      if (
        instance.app?.config?.globalProperties.$bridge &&
        instance.app?.config?.globalProperties.$bridge.isThirdParty
      ) {
        instance.app.config.globalProperties.$bridge?.reLogin();
        return;
      }
      // 默认事件
      if (window.location.pathname !== '/login') {
        bolRedirect &&
          router.push(`/login?redirect_uri=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      }
    }
  });
};

/* 是否正在刷新的标志 */
let isRefreshing = false;
/* 存储请求的数组 刷新token后重新发起请求 */
const refreshSubscribers = [];
function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}
function onRefreshed(token, val) {
  while (refreshSubscribers.length) {
    refreshSubscribers.pop()(token, val);
  }
}

axios.interceptors.request.use(
  (request) => {
    // 记录请求开始时间
    request.metaData = { startTime: Date.now() };
    const authInfo = getStorageAuthInfo() || {};
    const token = authInfo?.brmToken;
    const { expires_in, refresh_token, refresh_expires_in } = token || {};

    if (request.url.indexOf('{realm}') !== -1) {
      const realm = storage.get(LOCAL_STORAGE_KEY.REALM_KEY);
      request.url = request.url.replace('{realm}', realm || DEFAULT_REALM);
    }
    // 登录过期后 重新获取token
    if (
      isAccessTokenExpired(expires_in) &&
      token &&
      !request.url.includes('/protocol/openid-connect/token') &&
      !request.url.includes('/protocol/openid-connect/logout') &&
      !isAccessTokenExpired(refresh_expires_in)
    ) {
      if (!isRefreshing) {
        isRefreshing = true;
        // 跨标签页互斥：多个标签页同时过期时只让一个真正去换 token，其余复用结果，
        // 否则 refresh_token 轮换会让后到者失败并误报登录失效
        refreshAccessTokenExclusively(() =>
          store
            .dispatch('refreshTokenAction', {
              refreshToken: refresh_token,
              grantType: 'refresh_token',
              client_id: 'pc-admin-web'
            })
            .then((res) => {
              // 业务码非 0 时拿不到可用的新 token，转入下面的失败分支统一处理
              if (res.code !== 0) return Promise.reject(res);
              return res.data.accessToken;
            })
        )
          .then((accessToken) => {
            onRefreshed(accessToken, 1);
          })
          .catch((error) => {
            // 先弹登录失效提示再放行队列：alertFunc 会置位 isLoginOut，
            // 让下面被 reject 的排队请求不再重复弹出各自的错误提示
            alertContext = { scene: ALERT_SCENE.TOKEN_REFRESH_FAILED, config: request };
            alertFunc(
              error?.code || 201,
              error?.msg || i18n.global.t('common.tip.login_invalid'),
              true,
              i18n.global.t('common.button.go_to'),
              false
            );
            // 放行等待中的请求，避免它们因为拿不到刷新结果而永远挂起
            onRefreshed('', 0);
          })
          .finally(() => {
            isRefreshing = false;
          });
      }

      const retry = new Promise((resolve, reject) => {
        subscribeTokenRefresh((access_token, val) => {
          if (val === 1) {
            request.headers.Authorization = `${token.tokenType} ${access_token}`;
            Object.assign(request.headers, getOverseaHeader(true));
            resolve(request);
          } else {
            reject(request);
          }
        });
      });
      return retry;
    }

    // 获取外部服务的JWT接口
    if (request.url.includes(AUTH)) {
      return request;
    }

    if (token && token.code === '400') {
      // 无权限
      return Promise.reject({
        data: {
          code: 10086,
          msg: '未开通服务，暂无权限'
        }
      });
    }

    // 接口是否上报时间耗时
    if (request?.needTime) {
      const requestId = generateGUID();
      request.__requestId = requestId;

      const startEvent = new CustomEvent('httpLoadStart', {
        detail: {
          requestId,
          url: request.url,
          ...request,
          startTime: Date.now()
        }
      });
      window.dispatchEvent(startEvent);
    }

    if (token && request.url.includes('/protocol/openid-connect/token')) return request;

    Object.assign(request.headers, getOverseaHeader(true));

    if (token && !request.noAuthorization) {
      request.headers.Authorization =
        request.headers.Authorization || `${token.tokenType} ${token.access_token || token.accessToken || token.token}`;
      request.headers.realm = request.headers.realm || token.goId || storage.get(LOCAL_STORAGE_KEY.REALM_KEY);
    }

    const release = getCookie('release');
    if (release) {
      request.headers.release = release;
    }
    const symbol = getCookie('symbol');
    if (symbol) {
      request.headers.symbol = symbol;
    }
    // 品牌渠道 动态判断 mac 或 windows
    request.headers.brandChannel = isMac ? 'mac' : isWindows ? 'windows' : '';
    const language =
      i18n.global.locale.value ||
      getCookie(LOCAL_STORAGE_KEY.LANGUAGE) ||
      localStorage.getItem('lang') ||
      navigator.language;
    request.headers.language = language;
    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);
// 白名单code
const whiteCode = [1001];

axios.interceptors.response.use((response) => {
  if (response?.headers?.k) {
    try {
      response.data = JSON.parse(decryptRequest(response.headers.k, response.data, REQUESTSALT));
    } catch (err) {
      slsLogger.xhr({
        ...err.config,
        api: err.config.url,
        response: err.data,
        status: err.status,
        responseHeaders: JSON.stringify(err.request.getAllResponseHeaders()),
        message: 'api解析失败' + REQUESTSALT
      });
    }
  }

  const { status, data, config } = response;

  // 记录请求结束时间
  response.config.metaData.endTime = new Date();
  // 计算请求响应耗时
  response.costTime = response.config.metaData.endTime - response.config.metaData.startTime;
  handleResponseTrack(response);

  // 采集日志
  if ([201, 403, 404, 500].includes(+data?.code) && isElectron) {
    logger.error(
      JSON.stringify({
        url: config.url,
        response: config.data,
        header: {
          Authorization: config.headers?.authorization || config.headers?.Authorization,
          language: config.headers?.language || config.headers?.Language,
          uid: config.headers?.id || config.headers?.uid,
          realm: config.headers?.realm || config.headers?.Realm
        },
        params: config.params
      })
    );
  }

  if (status === 200) {
    if (typeof data == 'object' && (data instanceof Blob || data instanceof ArrayBuffer))
      return Promise.resolve(response);
    if (
      (data && data.code != 0 && !whiteCode.includes(data.code) && !data.status) ||
      (data.status && data.status !== '200')
    ) {
      alertContext = { scene: ALERT_SCENE.RESPONSE_BODY, config };
      alertFunc(
        data.code,
        data.errordesc || data.msg || data.message || i18n.global.t('common.tip.service_unavailable'),
        true,
        [201, 401, 403, 400].includes(data.code)
          ? i18n.global.t('common.button.go_to')
          : i18n.global.t('common.button.confirm'),
        // 40001 属于业务接口，不需要刷新页面
        ![201, 401, 403, 400].includes(data.code)
      );
      return Promise.reject(response);
    } else {
      return Promise.resolve(response);
    }
  }
}, handleResponseError);

function handleResponseError(err) {
  // 请求拦截器直接 reject 时（如 token 刷新失败）拿到的是 request 本身，没有 err.config，
  // 此时没有响应耗时可统计，直接透传错误
  if (!err?.config?.metaData) return Promise.reject(err);
  err.config.metaData.endTime = new Date();
  err.costTime = err.config.metaData.endTime - err.config.metaData.startTime;
  handleResponseTrack(err);
  return Promise.reject(err);
}

function Axios() {
  this.requestQueue = [];
}

Axios.prototype.success = function (res) {
  networkErrorToastShown = false;
  res.config = res.config || {};
  if (res.config?.needTime) {
    const endEvent = new CustomEvent('httpLoadEnd', {
      detail: {
        ...res.config,
        requestId: res.config.__requestId,
        url: res.config.url,
        endTime: Date.now(),
        response: res.data,
        status: res.status,
        responseHeaders: JSON.stringify(res.request.getAllResponseHeaders())
      }
    });
    window.dispatchEvent(endEvent);
  } else if (!res.config?.hideLogger) {
    slsLogger.xhr({
      ...res.config,
      api: res.config.url,
      response: res.data,
      status: res.status,
      responseHeaders: JSON.stringify(res.request.getAllResponseHeaders())
    });
  }

  const code = ~~res.data?.errorcode || ~~res.data.code;

  // 导出列表数据从请求头获取文件名
  const fileName = res.headers['content-disposition'];
  if (fileName) {
    res.data.fileName = fileName;
  }

  // code === 0 为成功，直接返回，其余转错误处理
  return code && res.config.captureError ? Promise.reject(res) : res.data;
};

Axios.prototype.error = function (err) {
  if (err.response || (err.status !== undefined && err.code !== 'ERR_NETWORK')) {
    networkErrorToastShown = false;
  }
  if (err.code === 'ERR_NETWORK') {
    // 默认请求超时
    const response = {
      errcode: 500,
      errmsg: i18n.global.t('common.tip.network_error')
    };

    let hideMessage = false;
    if (Object.prototype.hasOwnProperty.call(err, 'hideMessage')) {
      hideMessage = err.hideMessage;
    } else if (err.config) {
      hideMessage = err.config?.hideMessage || false;
    }
    // isLoginOut 限制多次请求只输出一次错误提示；networkErrorToastShown 限制 ERR_NETWORK 全周期只提醒一次直至网络恢复
    if (!isLoginOut && !hideMessage && !networkErrorToastShown) {
      networkErrorToastShown = true;
      ElMessage({
        showClose: true,
        message: response.errmsg,
        type: 'error'
      });
    }
    return Promise.reject(response);
  }
  err.config = err.config || {};
  if (err.config?.hideLogger && !err.config?.hideErrorLogger) {
    slsLogger.xhr({
      ...err.config,
      api: err.config.url,
      response: err.data,
      status: err.status,
      responseHeaders: JSON.stringify(err.request.getAllResponseHeaders())
    });
  }

  if (err instanceof AxiosModules.Cancel) {
    return Promise.reject({
      code: 'canceled'
    });
  }
  // 默认请求超时
  let response = {
    errcode: 500,
    errmsg: i18n.global.t('common.tip.network_error')
  };

  // 返回请求500错误
  if (err.response && (err.response.status === 500 || err.response.status === 404)) {
    response = {
      errcode: 500,
      errmsg: i18n.global.t('common.tip.network_error')
    };
  }
  // Authorization过期
  if (err.response && err.response.status === 403) {
    response = {
      errcode: 403,
      errmsg: i18n.global.t('common.tip.login_invalid')
    };
  }

  // 对返回值赋值
  Object.assign(response, err.data);

  // 处理errorcode 和 code
  response.errcode = response.code || response.errcode;
  response.errmsg = response.msg || response.errmsg;

  const code = ~~response.errcode;

  let hideMessage = false;
  if (Object.prototype.hasOwnProperty.call(err, 'hideMessage')) {
    hideMessage = err.hideMessage;
  } else if (err.config) {
    hideMessage = err.config.hideMessage;
  }
  // isLoginOut 限制多次请求只输出一次错误提示
  if (!isLoginOut && !hideMessage) {
    alertContext = { scene: ALERT_SCENE.HTTP_ERROR, config: err.config };
    switch (code) {
      case 500:
      case 404:
      case 10086:
      case 80001:
        alertFunc(code, response.errmsg, false);
        break;
      case 201:
      case 20101:
      case 20102:
        store.commit('setIsLogin', false);

        // 错误信息映射表 - 后端中文转前端i18n(前端临时处理)
        const errorMsgMap = {
          '该账号已在其他设备登录，为保证账号安全，请重新登录': 'common.tip.account_logged_in_elsewhere',
          '您的账号登录状态已失效，请重新登录': 'common.tip.login_expired',
          '为保护账号安全，请重新登录': 'common.tip.login_required'
        };

        // 优先使用映射表翻译，否则使用后端返回值，都没有则使用默认兜底文案
        const errorMessage =
          response.errmsg && errorMsgMap[response.errmsg]
            ? i18n.global.t(errorMsgMap[response.errmsg])
            : response?.errmsg || response?.errordesc || i18n.global.t('common.tip.login_expired');

        alertFunc(code, errorMessage, true, i18n.global.t('common.button.go_to'), async () => {
          isLoginOut = false;
          if (callback) return callback();
          await store.dispatch('logoutAction');

          const cooperationInfo = storage.get(LOCAL_STORAGE_KEY.COOPERATION, true);
          // 判断本地是否存在用户登录信息 有则自动登录
          if (cooperationInfo && cooperationInfo.userInfo && cooperationInfo.userChannel) {
            storage.clear();
            const hasQuery = window.location.search;
            const query = `${hasQuery ? '&' : '?'}userInfo=${encodeURIComponent(
              cooperationInfo.userInfo
            )}&userChannel=${cooperationInfo.userChannel}`;
            return (window.location.href = `${window.location.href}${query}`);
          }

          refreshSubscribers.length = 0;

          // 合作机构存在自定义跳转事件则执行
          if (
            instance.app.config.globalProperties.$bridge &&
            instance.app.config.globalProperties.$bridge.isThirdParty
          ) {
            instance.app.config.globalProperties.$bridge?.reLogin();
            return;
          }
          // 默认事件
          if (window.location.pathname !== '/login') {
            const redirectUrl = encodeURIComponent(window.location.pathname + window.location.search);
            if (isOverseasEnv()) {
              location.href = `${ENV_DATA.VITE_OVERSEAS_LOGIN_URL}?redirect_uri=${redirectUrl}`;
            } else {
              router.push(`/login?redirect_uri=${redirectUrl}`);
            }
          }
        });
        break;
      case 1971:
        alertFunc(code, i18n.global.t('common.tip.account_not_exist'), true);
        break;
      case 1976:
        alertFunc(code, i18n.global.t('common.tip.account_not_exist'), true);
        break;

      case 403:
        store.commit('setIsLogin', false);
        //从缓存中获取登录信息刷新页面
        const func = () => {
          // 判断本地是否存在用户登录信息 有则自动登录
          const cooperationInfo = storage.get(LOCAL_STORAGE_KEY.COOPERATION, true);
          if (cooperationInfo && cooperationInfo.userInfo && cooperationInfo.userChannel) {
            storage.clear();
            const hasQuery = window.location.search;
            const query = `${hasQuery ? '&' : '?'}userInfo=${encodeURIComponent(
              cooperationInfo.userInfo
            )}&userChannel=${cooperationInfo.userChannel}`;
            return (window.location.href = `${window.location.href}${query}`);
          } else {
            window.location.href = '/thirdparty/instituteNotFound';
          }
        };
        const callback = router.currentRoute.value.path.includes('/thirdparty') ? func : null;
        alertFunc(
          code,
          i18n.global.t('common.tip.login_invalid'),
          true,
          i18n.global.t('common.button.go_to'),
          false,
          callback
        );
        break;
      case 401:
        ElMessage.error(i18n.global.t('common.tip.no_permission'));
        break;
      // 验证码过期
      case 1001:
        // ElMessage.error('验证码已过期');
        break;
      // 验证码错误
      case 1002:
        //   ElMessage.error('验证码不正确');
        break;
      default:
        ElMessage({
          showClose: true,
          message: response.errmsg,
          type: 'error'
        });
    }
  }

  return Promise.reject(response);
};

Axios.prototype.request = function (config) {
  return axios
    .request(config)
    .then((res) => this.success(res))
    .catch((err) => this.error(err));
};

Axios.prototype.post = function (url = '', body = {}, options = {}) {
  // 初始化请求配置参数
  const axiosConfig = new AxiosConfig(url, body, Object.assign(options, { method: 'post' }));
  switch (axiosConfig.bodyType) {
    case 'form':
      axiosConfig.instance();

      // 固定请求参数放在 body
      Object.assign(axiosConfig.data, axiosConfig.initUrl());

      axiosConfig.headers['Content-Type'] = 'application/x-www-form-urlencoded;charset=utf-8';
      axiosConfig.transformRequest = [
        (data) => {
          return Qs.stringify(data);
        }
      ];
      break;
    case 'image':
    case 'file':
      axiosConfig.headers['Content-Type'] = 'multipart/form-data';
      axiosConfig.timeout = 1800000; // 超时时间为半小时

      const formData = new FormData();
      Object.keys(body).map((index) => {
        formData.append(index, body[index]);
      });
      axiosConfig.data = formData;
      break;
    default:
      // 默认json格式请求
      axiosConfig.instance();

      // 固定请求参数放在 headers
      Object.assign(axiosConfig.data, axiosConfig.initUrl());
  }
  if (typeof axiosConfig.progress === 'function') {
    axiosConfig.onUploadProgress = (progressEvent) => {
      axiosConfig.progress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
    };
  }
  return this.request(axiosConfig);
};

// eslint-disable-next-line no-unused-vars
Axios.prototype.get = function (url = '', body = {}, options = {}) {
  const axiosConfig = new AxiosConfig(...arguments);
  axiosConfig.instance();
  if (!axiosConfig?.data) {
    axiosConfig.data = {};
  }
  if (!/\.(xls|xlsx|pdf|png|svg|jpg|jpeg|txt|doc|docx)$/i.test(url)) {
    Object.assign(axiosConfig.data, axiosConfig.initUrl());
  }
  return this.request({
    url: axiosConfig.url,
    method: 'get',
    params: axiosConfig.data,
    responseType: options.responseType || 'json',
    headers: {
      ...options.headers,
      ...axiosConfig.headers
    },
    captureError: axiosConfig.captureError,
    hideMessage: options.hideMessage || false,
    hideLogger: axiosConfig.hideLogger,
    hideErrorLogger: axiosConfig.hideErrorLogger
  });
};

// eslint-disable-next-line no-unused-vars
Axios.prototype.delete = function (url = '', body = {}, options = {}) {
  const axiosConfig = new AxiosConfig(...arguments);
  axiosConfig.instance();
  if (!/\.(xls|xlsx|pdf|png|svg|jpg|jpeg|txt|doc|docx)$/i.test(url)) {
    Object.assign(axiosConfig.data, axiosConfig.initUrl());
  }
  return this.request({
    url: axiosConfig.url,
    method: 'delete',
    params: axiosConfig.data,
    responseType: options.responseType || 'json',
    headers: {
      ...options.headers,
      ...axiosConfig.headers
    },
    captureError: axiosConfig.captureError,
    hideMessage: options.hideMessage || false,
    hideLogger: axiosConfig.hideLogger,
    hideErrorLogger: axiosConfig.hideErrorLogger
  });
};

Axios.prototype.put = function (url = '', body = {}, options = {}) {
  // 初始化请求配置参数
  const axiosConfig = new AxiosConfig(url, body, Object.assign(options, { method: 'put' }));

  switch (axiosConfig.bodyType) {
    case 'form':
      axiosConfig.instance();

      // 固定请求参数放在 body
      Object.assign(axiosConfig.data, axiosConfig.initUrl());

      axiosConfig.headers['Content-Type'] = 'application/x-www-form-urlencoded;charset=utf-8';
      axiosConfig.transformRequest = [
        (data) => {
          return Qs.stringify(data);
        }
      ];
      break;
    case 'image':
    case 'file':
      axiosConfig.headers['Content-Type'] = 'multipart/form-data';
      axiosConfig.timeout = 1800000; // 超时时间为半小时

      const formData = new FormData();
      Object.keys(body).map((index) => {
        formData.append(index, body[index]);
      });
      axiosConfig.data = formData;
      break;
    default:
      // 默认json格式请求
      axiosConfig.instance();

      // 固定请求参数放在 headers
      Object.assign(axiosConfig.data, axiosConfig.initUrl());
  }

  return this.request(axiosConfig);
};
function AxiosConfig(url = '', body = {}, options = {}) {
  // 发送请求的地址
  this.url = url;

  // 发送请求的数据
  this.data = body;

  // 被发送的自定义请求头
  this.headers = options.headers || {};

  // 与请求一起发送的 URL 参数
  this.params = {};

  // 返回状态码非 0 时，使用全局处理错误，如果手动传入 captureError = false，则需要在 then 中手动处理
  this.captureError = true;

  // 错误信息
  this.hideMessage = false;
  // 接口请求不强制上传日志
  this.hideLogger = true;
  // 接口请求失败不传日志
  this.hideErrorLogger = false;

  // 自定义的一些参数
  // bodyType: String     请求参数数据类型 支持 json / form / file / image 默认json
  // hideMessage: Boolean 请求失败是否显示错误信息
  // timeout              请求超时时间
  // retry: Number        请求失败重试次数
  // retryDelay: Number   请求失败重试延迟时间
  // retryCount: Number   请求失败已经重试次数
  // needTrack: Boolean   请求响应后 是否需要埋点
  // trackParams: Object  请求响应埋点参数
  Object.assign(this, options);
}

AxiosConfig.prototype.instance = function () {
  // 默认设置token到请求头
  const storageAuthInfo = getStorageAuthInfo() || {};
  const userInfo = storageAuthInfo?.userinfo || {};
  const cooperationInfo = storage.get(LOCAL_STORAGE_KEY.COOPERATION, true);

  // 用户ID (非必填)
  if (!Object.prototype.hasOwnProperty.call(this.headers, 'uid') && userInfo && userInfo.id) {
    this.headers.uid = userInfo.id;
  }
  // 操作系统平台 ios android web
  this.headers.os = resolveOsHeader(isElectron ? 'pc-app' : 'brm');
  // os为web的扩展字段 pc端传值：pc , 手机端传值：mobile
  this.headers.c = 'pc';
  // os为web的扩展字段 web所处的环境 weixin qq comein minprogram
  this.headers.webenv = 'comein';
  // 手机操作系统
  this.headers.s = '';
  // 用户渠道 comein-app, comein-pc, comein-h5 , comein-miniprogram , comein-assist (非必填)
  this.headers.uc = cooperationInfo?.userChannel || 'comein-pc';
  // 浏览器
  this.headers.browse = window.navigator.appName;
  // user agent
  this.headers.ua = window.navigator.userAgent;
  // 外部展 示版本号(1.2.6)
  this.headers.b = isElectron ? '4.0.0800' : PORTVERSION;
  if (!('realm' in this.headers)) {
    // 从 localStorage 中获取 realm 值
    const realmValue = storage.get(LOCAL_STORAGE_KEY.REALM_KEY);
    // 如果 realmValue 不为空，则将其设置为 headers 对象的 realm 属性
    // 否则，将默认值设置为一个你指定的值
    this.headers.realm = realmValue ? (isNaN(realmValue) ? realmValue : Number(realmValue)) : DEFAULT_REALM;
  }

  /** 上述参数中非必选的参数，在能拿到信息的情况下都需要传入接口。 */

  // 排除不需要的参数
  const { excludes } = this;
  if (excludes && Array.isArray(excludes)) {
    excludes.forEach((key) => {
      delete this.headers[key];
    });
  }

  /** 上述参数中非必选的参数，在能拿到信息的情况下都需要传入接口。 */
  this.headers['Content-Type'] = 'application/json;charset=utf-8';

  // 错误信息
  this.hideMessage = false;
  // 接口请求不强制上传日志
  this.hideLogger = true;
  // 接口请求失败不传日志
  this.hideErrorLogger = false;
};

AxiosConfig.prototype.initUrl = function () {
  const arr = this.url.split('_');
  if (isOverseasEnv()) {
    this.url = '/api' + this.url;
  }
  if (arr.length !== 3) {
    return {};
  }

  return {
    app: arr[0],
    mod: arr[1],
    act: arr[2]
  };
};

export const instance = new Axios();

export default function install(Vue) {
  Vue.prototype.$axios = instance;
}
